import { useState, useCallback, useRef } from 'react';
import warehouseInventoryService from '../services/warehouseInventoryService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';
import { useWarehouseContext } from '../context/WarehouseContext';

/**
 * Hook: useWarehouseInventory
 * Provides list and record operations for warehouse inventory with simple dedupe/cache protections
 */
const useWarehouseInventory = () => {
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { showCreate, showUpdate, showDelete } = useMessageAlert();
  const { selectedWarehouseIds } = useWarehouseContext();

  // Local cache for warehouse summaries (per-warehouse) to avoid duplicate fetches during editing
  const summaryCacheRef = useRef(new Map());

  // Deduplication & in-flight tracking
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  const inFlightRef = useRef(new Set());

  const generateSignature = useCallback((params) => JSON.stringify(params || {}), []);

  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  const fetchInventory = useCallback(async (params = {}, options = {}) => {
    const { force = false } = options;
    const signature = generateSignature({ ...params, warehouse_ids: selectedWarehouseIds });

    if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
    if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

    setLoading(true);
    setError(null);
    inFlightRef.current.add(signature);

    try {
      const result = await warehouseInventoryService.getAllInventory(params, options);
      if (result.success) {
        const data = result.data;
        // support different shapes: array or { items, total, totalPages }
        if (Array.isArray(data)) {
          setInventories(data);
          setTotalPages(1);
          setTotalCount(data.length);
        } else if (Array.isArray(data.items)) {
          setInventories(data.items);
          setTotalPages(data.totalPages || Math.ceil((data.total || data.items.length) / (params.limit || 20)));
          setTotalCount(data.total || data.items.length);
        } else {
          setInventories(data || []);
          setTotalPages(0);
          setTotalCount(Array.isArray(data) ? data.length : 0);
        }

        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
        setInventories([]);
      }

      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      setInventories([]);
      return { success: false, error: errorMessage };
    } finally {
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

  const getInventoryById = useCallback(async (id, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.getInventoryById(id, options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const getLowStock = useCallback(async (params = {}, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.getLowStock(params, options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductDistribution = useCallback(async (product_size_id, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.getProductDistribution(product_size_id, options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const getWarehouseSummary = useCallback(async (warehouse_id, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      // Check local per-warehouse cache first unless force is requested
      const force = options && options.force;
      if (!force) {
        const cached = summaryCacheRef.current.get(String(warehouse_id));
        if (cached) {
          setLoading(false);
          return { success: true, data: cached, fromCache: true };
        }
      }

      const result = await warehouseInventoryService.getWarehouseSummary(warehouse_id, options);
      if (result.success && result.data) {
        try { summaryCacheRef.current.set(String(warehouse_id), result.data); } catch (e) {}
      }
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const getStats = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.getStats(options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const createInventory = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.createInventory(payload);
      if (result.success) {
        const createdData = result.data;
        
        // Handle both array (bulk creation with multiple products) and single object responses
        const createdItems = Array.isArray(createdData) ? createdData : [createdData];
        
        setInventories(prev => {
          const newItems = createdItems.map(newItem => {
            // create a copy to avoid mutating service result
            const item = { ...newItem };
            // If the server didn't return nested warehouse_details, try to reuse from existing list
            if (!item.warehouse_details && item.warehouse_id) {
              const found = prev.find(p => Number(p.warehouse_id) === Number(item.warehouse_id) || (p.warehouse_details && Number(p.warehouse_details.id) === Number(item.warehouse_id)));
              if (found && found.warehouse_details) {
                item.warehouse_details = found.warehouse_details;
              }
            }
            // Similarly preserve product_details if missing by matching product_size_id
            if (!item.product_details && item.product_size_id) {
              const foundP = prev.find(p => Number(p.product_size_id) === Number(item.product_size_id) || (p.product_details && Number(p.product_details.id) === Number(item.product_size_id)));
              if (foundP && foundP.product_details) {
                item.product_details = foundP.product_details;
              }
            }
            return item;
          });
          return [...newItems, ...prev];
        });
        
        setTotalCount(c => c + createdItems.length);
        
        // Invalidate cached warehouse summaries for affected warehouses
        const warehouseIds = new Set();
        for (const item of createdItems) {
          if (item?.warehouse_id) warehouseIds.add(String(item.warehouse_id));
        }
        try { 
          for (const wid of warehouseIds) {
            summaryCacheRef.current.delete(wid);
          }
        } catch (e) {}
        
        // Show notification
        const createdLabel = createdItems.length === 1 
          ? createdItems[0]?.id 
          : `${createdItems.length} items`;
        showCreate(i18n.t('warehouseInventory.notifications.created', { id: String(createdLabel) }));
        // Refresh aggregated product-view to ensure table shows updated product-level summaries
        try {
          await fetchInventory({}, { force: true });
        } catch (e) {
          // swallow refresh errors — created items are already merged locally
        }
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showCreate, fetchInventory]);
  

  const adjustStock = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.adjustStock(id, payload);
      if (result.success) {
        // update local list if present
        setInventories(prev => prev.map(i => (String(i.id) === String(id) ? { ...i, ...result.data } : i)));
  try { if (result.data?.warehouse_id) summaryCacheRef.current.delete(String(result.data.warehouse_id)); } catch (e) {}
  showUpdate(i18n.t('warehouseInventory.notifications.stockUpdated', { id: String(id) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showUpdate]);

  const transferStock = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.transferStock(payload);
      if (result.success) {
        // Invalidate summaries for any warehouses affected by the transfer
        try {
          if (Array.isArray(result.data?.updated)) {
            for (const upd of result.data.updated) {
              if (upd?.warehouse_id) summaryCacheRef.current.delete(String(upd.warehouse_id));
              if (upd?.from_warehouse_id) summaryCacheRef.current.delete(String(upd.from_warehouse_id));
              if (upd?.to_warehouse_id) summaryCacheRef.current.delete(String(upd.to_warehouse_id));
            }
          }
        } catch (e) {}
        showUpdate(i18n.t('warehouseInventory.notifications.transferred'));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showUpdate]);

  const updateInventory = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const result = await warehouseInventoryService.updateInventory(id, payload);
      if (result.success) {
        const data = result.data;
        // Handle various response shapes: { updated: [...] }, array of records, or single record
        let updatedItems = [];
        if (data && Array.isArray(data.updated) && data.updated.length) {
          updatedItems = data.updated;
        } else if (Array.isArray(data) && data.length) {
          updatedItems = data;
        } else if (data && data.id) {
          updatedItems = [data];
        }

        if (updatedItems.length > 0) {
          setInventories(prev => {
            const map = new Map(prev.map(i => [String(i.id), { ...i }]));
            for (const upd of updatedItems) {
              const key = String(upd.id);
              const existing = map.get(key) || {};
              const merged = { ...existing, ...upd };
              if (!upd.warehouse_details && existing.warehouse_details) merged.warehouse_details = existing.warehouse_details;
              if (!upd.product_details && existing.product_details) merged.product_details = existing.product_details;
              map.set(key, merged);
            }
            return Array.from(map.values());
          });

          // Invalidate cached warehouse summaries for affected warehouses
          try {
            for (const upd of updatedItems) {
              if (upd?.warehouse_id) summaryCacheRef.current.delete(String(upd.warehouse_id));
            }
          } catch (e) {}
        } else {
          // Unknown response shape: refresh the inventory list to be safe
          try { await fetchInventory({}, { force: true }); } catch (e) {}
        }

        showUpdate(i18n.t('warehouseInventory.notifications.updated', { id: String(id) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showUpdate, fetchInventory]);

  const deleteInventory = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      // Capture existing item's warehouse id so we can invalidate its summary after delete
      const existing = inventories.find(i => String(i.id) === String(id));
      const existingWid = existing?.warehouse_id ?? existing?.warehouse_details?.id ?? null;

      const result = await warehouseInventoryService.deleteInventory(id);
      if (result.success) {
        setInventories(prev => prev.filter(i => String(i.id) !== String(id)));
        setTotalCount(prev => Math.max(0, prev - 1));
  try { if (existingWid) summaryCacheRef.current.delete(String(existingWid)); } catch (e) {}
  showDelete(i18n.t('warehouseInventory.notifications.deleted', { id: String(id) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [showDelete, inventories]);

  /**
   * Export warehouse inventory by Sub Category as a CSV file (product row ×
   * warehouse column). Triggers a browser download on success. SUPER_ADMIN only.
   *
   * @param {Object} params
   * @param {Array<number>} [params.catalogue_ids] Sub Category ids (omit = all)
   * @param {Array<number>} [params.warehouse_ids] Warehouse columns (omit = user's scope)
   * @param {'stock'|'available'} [params.value] Which quantity each cell shows
   */
  const exportBySubcategory = useCallback(async (params = {}) => {
    const result = await warehouseInventoryService.exportBySubcategory(params);
    if (!result.success) {
      return { success: false, error: result.error };
    }

    const disposition = result.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = match ? match[1].replace(/['"]/g, '') : 'inventory_by_subcategory.csv';

    const blobUrl = URL.createObjectURL(result.data);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);

    return { success: true };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    // state
    inventories,
    loading,
    error,
    totalPages,
    totalCount,
    // actions
    fetchInventory,
    getInventoryById,
    getLowStock,
    getProductDistribution,
    getWarehouseSummary,
    getStats,
    createInventory,
    adjustStock,
    transferStock,
    updateInventory,
    deleteInventory,
    exportBySubcategory,
    clearError,
  };
};

export default useWarehouseInventory;
